<template>
  <EModal
    v-if="isCompanyNameValid && isValidLabel"
    :active="active"
    :full-width="true"
    class="climate-pledge-modal"
    v-bind="$attrs"
    v-on="$listeners"
    @modal-display="trackDisplay"
  >
    <div class="climate-pledge-modal__content">
      <div class="climate-pledge-modal__header climate-pledge-modal__content-item">
        <div class="climate-pledge-modal__icon-container">
          <ELeadingIcon v-if="isLeading" aria-hidden="true" />
          <EGoodIcon v-if="isGood" aria-hidden="true" />
          <EModestIcon v-if="isModest" aria-hidden="true" />
          <EweakIcon v-if="isWeak" aria-hidden="true" />
          <EFailing v-if="isFailing" aria-hidden="true" />
        </div>
        <EHeading as="h3" class="climate-pledge-modal__headline">
          {{ $t(`web.green.climatepledge.${companyInfo.name}.headline`) }}
        </EHeading>
        <p data-test-id="climate-pledge-modal-rating" class="climate-pledge-modal__rating">
          {{ pledge.score }} : {{ pledge.type }}
        </p>
      </div>
      <div class="climate-pledge-modal__body">
        <div
          v-if="`$t('web.green.climatepledge.${companyInfo.name}.summary')`"
          class="climate-pledge-modal__content-item climate-pledge-modal__content-item-summary"
        >
          <p class="climate-pledge-modal__text">
            {{ $t(`web.green.climatepledge.${companyInfo.name}.summary`) }}
          </p>
        </div>
        <div
          v-if="`$t('web.green.climatepledge.${companyInfo.name}.summaryFollowUp')`"
          class="climate-pledge-modal__content-item climate-pledge-modal__content-item-followup"
        >
          <p class="climate-pledge-modal__text">
            {{ $t(`web.green.climatepledge.${companyInfo.name}.summaryFollowUp`) }}
          </p>
        </div>
        <div
          class="climate-pledge-modal__content-item climate-pledge-modal__content-item-how-we-rate"
        >
          <ELink
            v-analytics.click="trackHowWeRateClick"
            :href="howWeRateCompanies"
            rel="noopener"
            target="_blank"
            data-test-id="how-we-rate-link"
            class="climate-pledge-modal__link climate-pledge-modal__link--blue"
          >
            {{ $t('web.green.climatepledge.learnHowWeRateCompanies') }} <ELinkIcon />
          </ELink>
          <EButton
            v-if="isTakeActionEnabled && isAmazon"
            variant="solid-green"
            size="s"
            class="take-action-button"
            @click="showActionModal"
          >
            {{ actionText }}
          </EButton>
        </div>
      </div>
    </div>
    <EWave class="climate-pledge-modal__footer-wave" aria-hidden="true" />
    <footer class="climate-pledge-modal__footer">
      <div class="climate-pledge-modal__footer-source">
        <span v-if="sources.length" class="climate-pledge-modal__footer-source-label">
          {{
            sources.length > 1 ?
              $t('web.green.climatepledge.sourcesText') :
              $t('web.green.climatepledge.sourceText')
          }}:
        </span>
        <ELink
          v-for="(source) in sources"
          :key="source.title"
          v-analytics.click="trackSourceClick(source.url)"
          :href="source.url"
          rel="noopener"
          color="bare"
          target="_blank"
          class="climate-pledge-modal__footer-link"
        >
          {{ source.title }}
        </ELink>
      </div>
      <ELink
        as="button"
        class="feedback-link-cmp"
        color="secondary"
        data-test-id="cp-feedback-link"
        @click="onFeedbackLinkClick"
      >
        {{ $t('common.footer.feedback') }}
      </ELink>
    </footer>
  </EModal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EModal from '~~/common/vue/components/modal';
import EHeading from '~~/common/vue/components/heading';
import ELink from '~~/common/vue/components/link';
import EButton from '~~/common/vue/components/button';
import { CLIMATE_PLEDGE_RATINGS_FAQ } from '~~/common/js/constants/urls';
import {
  climatePledgeOverlayFaqClick,
  climatePledgeOverlaySourceClick,
  climatePledgeOverlayDisplay,
  climatePledgeUserActionClick,
} from '~~/search/common/js/client/analytics/climate-pledge-ratings';

export default {
  name: 'EClimatePledgeModal',
  components: {
    EModal,
    EHeading,
    ELink,
    EButton,
    EWave: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "climate-pledge-modal-footer-wave" */
      './assets/climate-pledge-modal-footer-wave.svg'
    ),
    ELeadingIcon: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "leading" */
      './assets/leading.svg'
    ),
    EGoodIcon: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "good" */
      './assets/good.svg'
    ),
    EModestIcon: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "modest" */
      './assets/modest.svg'
    ),
    EweakIcon: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "weak" */
      './assets/weak.svg'
    ),
    EFailing: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "failing" */
      './assets/failing.svg'
    ),
    ELinkIcon: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "link-icon" */
      './assets/link-icon.svg'
    ),
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    companyInfo: {
      type: Object,
      required: true,
    },
    rating: {
      type: String,
      required: true,
    },
    triggerLocation: {
      type: String,
      default: '',
      validator: (location) => ['infobox', 'tooltip', ''].includes(location),
    },
  },
  data() {
    return {
      feedbackActive: false,
    };
  },
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapGetters('unleash', ['getFeatureToggle']),
    isLeading() {
      return this.rating === 'CLIMATE_PLEDGE_RATING_A';
    },
    isGood() {
      return this.rating === 'CLIMATE_PLEDGE_RATING_B';
    },
    isModest() {
      return this.rating === 'CLIMATE_PLEDGE_RATING_C';
    },
    isWeak() {
      return this.rating === 'CLIMATE_PLEDGE_RATING_D';
    },
    isFailing() {
      return this.rating === 'CLIMATE_PLEDGE_RATING_F';
    },
    pledge() {
      return this.expectedRatings[this.rating];
    },
    howWeRateCompanies() {
      return CLIMATE_PLEDGE_RATINGS_FAQ;
    },
    isValidLabel() {
      return Object.keys(this.expectedRatings).includes(this.rating);
    },
    isCompanyNameValid() {
      return !!this.companyInfo.name;
    },
    sources() {
      const sources = [];
      for (let index = 0; index < this.companyInfo.sources.length; index += 1) {
        const searchTitle = `title${index + 1}`;
        const source = this.companyInfo.sources.find((item) => !!item[searchTitle]);
        sources.push({
          title: source[searchTitle],
          url: source.url,
        });
      }
      return sources;
    },
    expectedRatings() {
      return {
        CLIMATE_PLEDGE_RATING_A: {
          score: 'A',
          type: this.$t('web.green.climatepledge.type.leading'),
        },
        CLIMATE_PLEDGE_RATING_B: {
          score: 'B',
          type: this.$t('web.green.climatepledge.type.good'),
        },
        CLIMATE_PLEDGE_RATING_C: {
          score: 'C',
          type: this.$t('web.green.climatepledge.type.modest'),
        },
        CLIMATE_PLEDGE_RATING_D: {
          score: 'D',
          type: this.$t('web.green.climatepledge.type.weak'),
        },
        CLIMATE_PLEDGE_RATING_F: {
          score: 'F',
          type: this.$t('web.green.climatepledge.type.failing'),
        },
      };
    },
    trackHowWeRateClick() {
      return climatePledgeOverlayFaqClick;
    },
    isTakeActionEnabled() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ||
            this.getFeatureToggle('cpr_action_button_text')?.value === 'take_action';
    },
    actionText() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ? 'Share rating' : 'Take action';
    },
    isAmazon() {
      const orgName = this.companyInfo.parentName || this.companyInfo.name;
      return orgName === 'amazon';
    },
  },
  methods: {
    trackSourceClick(url) {
      return climatePledgeOverlaySourceClick(url);
    },
    trackDisplay() {
      const { triggerLocation } = this;
      this.$track(climatePledgeOverlayDisplay(triggerLocation));
    },
    onFeedbackLinkClick() {
      this.$emit('cp-feedback-link-click');
    },
    showActionModal() {
      const label = this.actionText?.split(' ')?.join('_');
      this.$track(climatePledgeUserActionClick(label, 'modal'));
      this.$emit('show-cpr-action-modal');
    },
  },
};
</script>
<style lang="scss" scoped>
  .climate-pledge-modal__content {
    padding: 0 $space-3l;
    cursor: default;
  }

  .climate-pledge-modal__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $space-2s;
  }

  .climate-pledge-modal__body {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .climate-pledge-modal__headline {
    font-size: $font-3l;
    font-weight: $font-weight-m;
    color: var(--color-text-primary);
    margin-top: $space-1s;
  }

  .climate-pledge-modal__text {
    font-size: $font-l;
    font-weight: $font-weight-m;
    color: var(--color-text-primary);
    margin: 0;
  }

  .climate-pledge-modal__link {
    color: var(--color-link-results-default);
    margin-bottom: $space-2s;
  }

  .climate-pledge-modal__rating {
    font-size: $font-1l;
    font-weight: $font-weight-1l;
    color: var(--color-text-primary);
    margin: 0;
    margin-top: $space-1s;
  }

  .climate-pledge-modal__content-item {
    &-summary {
      margin-top: $space-1l;
    }

    &-followup {
      margin-top: $space-m;
    }

    &-how-we-rate {
      margin-top: $space-m;
    }
  }

  .climate-pledge-modal__icon-container {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    margin-top: $space-2s;
  }

  .climate-pledge-modal__footer-wave {
    width: 100%;
    height: auto;
    margin-top: $space-4l;
    color: var(--color-background-quaternary);
  }

  .climate-pledge-modal__footer {
    width: 100%;
    background: var(--color-background-quaternary);
    padding: 0 $space-3l $space-3l $space-3l;
    border-radius: 0 0 $border-radius-l $border-radius-l;
    display: flex;
    justify-content: space-between;
  }

  .climate-pledge-modal__footer-source {
    width: 80%;
    height: 100%;
  }

  .climate-pledge-modal__footer-source-label {
    display: inline-block;
    color: var(--color-text-secondary);
    font-size: $font-s;
    font-weight: $font-weight-m;
  }

  .climate-pledge-modal__footer-link {
    display: inline-block;
    font-size: $font-s;
    font-weight: $font-weight-m;
  }

  .climate-pledge-modal__feedback-link {
    display: flex;
    align-items: flex-end;
  }

  .take-action-button {
    margin-left: $space-s;
  }
</style>
